/**
 * Track on this device (T2, T6, T7 — 04.10.2026): a workspace's mirror as a
 * LOCAL git repository with no remote, and adding a remote to it later.
 *
 * - trackLocally / saveVersion: refresh the mirror → secret scan → commit.
 *   Only `git_init_or_open`, `git_merge_abort` (a no-op on a clean repo) and
 *   `git_commit_all` touch the repository; nothing goes to a network.
 * - addRemote: the first-connect sequence on top of the existing history —
 *   create-if-missing (GitHub/GitLab) → set the remote → fetch → refuse a
 *   repository that already holds ANOTHER workspace → commit → push → on
 *   "diverged" merge the remote (e.g. its README commit) and push again.
 *   Local commits are never rewritten: no reset, no force push.
 *
 * Kept out of workspaceGitSync.js (small focused files); the remote paths
 * there are unchanged.
 */
import { invoke } from '@tauri-apps/api/core';
import { mirrorAbsDir } from '../workspaceMirror';
import { refreshMirror, scanMirrorForSecrets } from '../workspaceGitSync';
import { commitMessageFor } from './mirrorChanges';
import { getProvider, normalizeGitLink, LOCAL_PROVIDER_ID } from './providers';
import { DEFAULT_BRANCH, branchOfLink, requireToken, credArgs, authorFor } from './gitAuth';
import { APP_NAME } from '../../utils/appInfo';

export const REMOTE_HAS_WORKSPACE = 'REMOTE_HAS_WORKSPACE';

/** A fresh local link: no owner, repo, url, account or Vault item. */
export function localLink(since = Date.now()) {
  return { provider: LOCAL_PROVIDER_ID, branch: DEFAULT_BRANCH, since };
}

export function isLocalLink(git) {
  return git?.provider === LOCAL_PROVIDER_ID;
}

const slash = (dir) => String(dir).replace(/\\/g, '/');

/**
 * Refresh the mirror and commit it. The repository is opened — or created
 * again when it is missing (the folder was deleted by hand): the next save
 * starts a fresh history. A merge left over from an earlier attempt is
 * cancelled first, before the mirror is written, so it never eats the save.
 * @returns {Promise<{status:'secrets', findings:object[]}|{status:'ok', commit:string|null}>}
 */
export async function commitMirror(workspace, snapshot, { allowSecrets = false, message, author } = {}) {
  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOfLink(workspace.git);
  await invoke('git_init_or_open', { repoPath: dir, branch });
  await invoke('git_merge_abort', { repoPath: dir });
  await refreshMirror(workspace, snapshot);
  // Card 5: nothing that looks like a credential is committed unless the
  // user said so after seeing exactly where (file:line, never the value).
  if (!allowSecrets) {
    const findings = await scanMirrorForSecrets(dir);
    if (findings.length > 0) return { status: 'secrets', findings };
  }
  const who = author || (await authorFor(getProvider(LOCAL_PROVIDER_ID), workspace.git));
  const commit = await invoke('git_commit_all', {
    repoPath: dir,
    message: await commitMessageFor(slash(dir), message || `Save workspace in ${APP_NAME}`),
    ...who,
  });
  return { status: 'ok', commit: commit || null };
}

/**
 * Start tracking a workspace on this device: its first version.
 * @returns {Promise<{status:'ok'|'secrets', git:object, findings?:object[]}>}
 */
export async function trackLocally(workspace, { snapshot, allowSecrets = false } = {}) {
  const git = { ...localLink(), ...(allowSecrets ? { secretsAllowed: true } : {}) };
  const ws = { ...workspace, git };
  const result = await commitMirror(ws, snapshot, { allowSecrets, message: `Start tracking in ${APP_NAME}` });
  if (result.status === 'secrets') return { status: 'secrets', findings: result.findings, git };
  return { status: 'ok', git, commit: result.commit };
}

/**
 * "Save version": commit what is in the app now. `commit` is null when there
 * was nothing new to save.
 * @returns {Promise<{status:'ok'|'secrets', commit?:string|null, findings?:object[], git:object}>}
 */
export async function saveVersion(workspace, snapshot, { allowSecrets = false } = {}) {
  if (!isLocalLink(workspace?.git)) throw new Error('NOT_LOCAL');
  const result = await commitMirror(workspace, snapshot, { allowSecrets });
  const git = allowSecrets && result.status === 'ok' ? { ...workspace.git, secretsAllowed: true } : workspace.git;
  return { ...result, git };
}

/** The `id:` a workspace manifest (apiboo.yaml) names, or null. */
export function manifestId(text) {
  if (typeof text !== 'string') return null;
  if (/^apiboo:[^\S\r\n]*scope[^\S\r\n]*$/m.test(text)) return null; // a boards & documents repository
  const m = text.match(/^id:[^\S\r\n]*["']?([^"'\r\n]*?)["']?[^\S\r\n]*$/m);
  return m && m[1] ? m[1] : null;
}

function refusal() {
  const err = new Error(
    'That repository already holds another workspace. Choose a new or empty repository, or clone it as a separate workspace.'
  );
  err.code = REMOTE_HAS_WORKSPACE;
  return err;
}

/**
 * Throws REMOTE_HAS_WORKSPACE when the fetched remote branch carries an
 * apiboo.yaml that names another workspace (or is not a workspace manifest).
 * A remote without apiboo.yaml (a fresh repository, a README) is fine.
 */
async function checkRemoteIdentity(dir, workspace, branch) {
  const st = await invoke('git_status', { repoPath: dir, remote: 'origin', branch });
  if (!st?.remoteOid) return;
  const theirs = await invoke('git_file_at', { repoPath: dir, oid: st.remoteOid, path: 'apiboo.yaml' });
  if (theirs == null) return;
  const ours = st.headOid ? await invoke('git_file_at', { repoPath: dir, oid: st.headOid, path: 'apiboo.yaml' }) : null;
  const ourId = manifestId(ours) || String(workspace.id);
  if (manifestId(theirs) !== ourId) throw refusal();
}

/**
 * Add a remote to a workspace tracked on this device and push its whole
 * history — the same commit ids, plus a merge when the remote already had
 * an unrelated commit (GitHub's / GitLab's README).
 * @returns {Promise<{status:'ok'|'secrets'|'conflicts'|'diverged', git:object, merged?:boolean, findings?, conflicts?}>}
 *   `git` is the new REMOTE link on 'ok' / 'conflicts', the unchanged local
 *   link otherwise. On REMOTE_HAS_WORKSPACE (thrown) the remote is removed
 *   again and nothing was pushed.
 */
export async function addRemote(
  workspace,
  {
    owner,
    repo,
    createIfMissing = true,
    isPrivate = true,
    snapshot,
    allowSecrets = false,
    provider: providerId,
    accountId,
    url: remoteUrl,
    vaultItemId,
    vaultItemName,
  } = {}
) {
  if (!isLocalLink(workspace?.git)) throw new Error('NOT_LOCAL');
  const provider = getProvider(providerId);
  if (provider.localOnly) throw new Error('Choose GitHub, GitLab or another git server.');
  const ctx = { provider: provider.id, accountId, url: remoteUrl, vaultItemId, vaultItemName };
  const token = await requireToken(provider, ctx);
  if (!owner || !repo) throw new Error('Repository owner and name are required');

  const dir = await mirrorAbsDir(workspace.id);
  const branch = branchOfLink(workspace.git);

  // The latest state is scanned before anything leaves this device.
  await invoke('git_init_or_open', { repoPath: dir, branch });
  await invoke('git_merge_abort', { repoPath: dir });
  await refreshMirror(workspace, snapshot);
  if (!allowSecrets) {
    const findings = await scanMirrorForSecrets(dir);
    if (findings.length > 0) return { status: 'secrets', findings, git: workspace.git };
  }

  if (createIfMissing && provider.abilities.createRepo) {
    await provider.ensureRepoExists(token, owner, repo, isPrivate, ctx);
  }
  const author = await authorFor(provider, ctx);
  const url = await provider.repoUrl(owner, repo, ctx);
  const cred = credArgs(provider, token, ctx);
  const git = normalizeGitLink(
    {
      owner,
      repo,
      branch,
      url,
      provider: provider.id,
      ...(vaultItemId ? { vaultItemId, vaultItemName: vaultItemName || '' } : {}),
      ...(workspace.git.since ? { since: workspace.git.since } : {}),
    },
    { accountId: accountId || author.authorName || undefined }
  );

  await invoke('git_set_remote', { repoPath: dir, name: 'origin', url });
  const dropRemote = () => invoke('git_remove_remote', { repoPath: dir, name: 'origin' }).catch(() => {});
  try {
    // An empty repository has no branch to fetch: not an error here. The
    // identity check then runs before any merge below.
    let checked = false;
    try {
      await invoke('git_fetch', { repoPath: dir, ...cred, remote: 'origin', branch });
      await checkRemoteIdentity(dir, workspace, branch);
      checked = true;
    } catch (e) {
      if (e?.code === REMOTE_HAS_WORKSPACE) throw e;
    }

    await invoke('git_commit_all', {
      repoPath: dir,
      message: await commitMessageFor(slash(dir), `Save workspace in ${APP_NAME}`),
      ...author,
    });

    let push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
    let merged = false;
    if (push.status === 'diverged') {
      if (!checked) {
        await invoke('git_fetch', { repoPath: dir, ...cred, remote: 'origin', branch });
        await checkRemoteIdentity(dir, workspace, branch);
      }
      // The remote's own first commit (a README) joins the history as a merge.
      const merge = await invoke('git_merge_remote', { repoPath: dir, ...cred, remote: 'origin', branch, ...author });
      if (merge.status === 'conflicts') return { status: 'conflicts', conflicts: merge.conflicts || [], git };
      merged = merge.status !== 'up_to_date';
      push = await invoke('git_push', { repoPath: dir, ...cred, remote: 'origin', branch });
      if (push.status !== 'ok') {
        await dropRemote();
        return { status: 'diverged', git: workspace.git };
      }
    }
    if (provider.abilities.tagWorkspace) provider.tagWorkspace(token, owner, repo, git);
    return { status: 'ok', git, merged };
  } catch (err) {
    // Anything that did not end in a push leaves the history local, as it was.
    await dropRemote();
    throw err;
  }
}
